import { XHRInstance } from "@sembark-travel/xhr"
import { ICabType } from "../../CabTypes"
import { IUser } from "../../Auth"
import { TTripDestination } from "../../TripDestinations"
import {
	ITransportService,
	TTransportServiceLocation,
} from "../../TransportServices"
import { TFormattedMoney } from "@sembark-travel/money"
import { formatDate, startOf } from "@sembark-travel/datetime-utils"
import { ITransportServicePricesFormValues } from "./NewItem"
import { TSheetFile, TSheetFileVersion, TSheetFolder } from "../store"

type TFileCreatePayload = {
	folder: number // ID of the Folder
	file_name: string // file's name (existing or new one)
	file_id?: number // optional existing file id (on update time required)
	file_description?: string // optional file description
	currency: string
	trip_destinations?: Array<number> // IDs of selected destinations
	timezone_offset: number // timezone offset of UTC, use the config.timezoneOffset
	seasons: Array<{
		name: string
		currency?: string
		intervals: Array<{
			start_date: string | null // date formatted as "YYYY-MM-DD"
			end_date: string | null // date formatted as "YYYY-MM-DD"
			days_of_week?: Array<
				"Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun"
			>
		}>
		cab_types: Array<string>
	}>
	data: Array<{
		transport_service_location_id?: number
		transport_services: Array<{
			transport_service_id?: number
			seasons: Array<{
				prices: Array<{ cab_type?: string; amount: number | null }>
			} | null> // The indexed of these must match with the season's indexes. Don't send the season ids.
			// NOTE: if there are multiple prices for the same transport service location and services for a given season and cab type, the last one will be sent back after save
		}>
	}>
}
export type TFileData = {
	id: number
	created_at: string
	created_by?: IUser
	currency: string
	trip_destinations: Array<TTripDestination>
	seasons: Array<TSeason>
	file_version?: TFileVersion
	prices: Array<TFilePrice>
	history?: Array<TFileData> // only available when loading full details of this file data. This is the list of all the saves for a single version
}
export type TFile = {
	id: number
	name: string // The File's Name
	description: string | null // Description of the file, optional
	created_at: string
	created_by?: IUser
	updated_at: string | null
	updated_by?: IUser
	folder?: TSheetFolder // Only available when loading full details of a file
	latest_version: TFileVersion
	versions?: Array<TFileVersion> // Only available when loading full details of a file
}
export type TFileVersion = {
	id: number
	is_draft: boolean // indicates that the version is in draft stage
	created_at: string
	created_by?: IUser
	uploaded_at: string | null
	uploaded_by?: IUser
	file: TFile
	data: TFileData
}
type TSeasonInterval = {
	start_date: string
	end_date: string
	days_of_week?: Array<"Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun">
	status?: string
}
export type TSeason = {
	id: number
	name: string
	currency: string
	for_bookings: boolean
	intervals: Array<TSeasonInterval>
	timezone_offset: number
	cab_types: Array<ICabType & { status?: string }> // existing cab type from the application
	status?: string
}
export type TFilePrice = {
	id: number
	transport_service_location: TTransportServiceLocation // already defined in Transport Services
	transport_services: Array<{
		id: number
		transport_service: ITransportService // already defined in Transport Services
		seasons: null | {
			[season_id: string]: {
				season_id: number
				prices: null | {
					[cab_type_id: string]: {
						cab_type_id: number
						prices: TFormattedMoney & {
							status?: string
							oldValue?: TFormattedMoney
						}
					}
				}
				status?: string
			}
		}
		status?: string
	}>
	status?: string
}
export type TComparisonSheetData = {
	seasons: TFileData["seasons"]
	prices: Array<{
		transport_service_location: Pick<TTransportServiceLocation, "id" | "name">
		transport_services: Array<{
			id: number
			seasons: TFilePrice["transport_services"][number]["seasons"]
			transport_service: Pick<
				ITransportService,
				| "id"
				| "service"
				| "distance"
				| "duration"
				| "duration_string"
				| "start_time_formatted"
				| "end_time_formatted"
			>
			status?: string
		}>
		status?: string
	}>
}
export function transportServicesPriceXHR(xhr: XHRInstance) {
	return {
		async getFileDetails(fileId: string): Promise<TSheetFile<TFileData>> {
			return xhr
				.get(`/data-drive/files/${fileId}`)
				.then((resp) => resp.data.data)
		},

		async getFileVersionDetails(
			fileVersionId: string
		): Promise<TSheetFileVersion<TFileData>> {
			return xhr
				.get(`/data-drive/file-versions/${fileVersionId}`)
				.then((resp) => resp.data)
		},

		async createFile(
			fileData: ITransportServicePricesFormValues,
			folderId: number,
			fileName: string,
			fileId?: number
		): Promise<TSheetFileVersion<TFileData>> {
			const {
				currency,
				timezone_offset,
				trip_destinations,
				seasons,
				transport_price_services,
			} = fileData
			const fileDataPayload: TFileCreatePayload = {
				folder: folderId,
				file_name: fileName,
				file_id: fileId,
				currency,
				timezone_offset,
				trip_destinations: trip_destinations?.map((trip) => trip.id),
				seasons: seasons.map((season) => {
					return {
						name: season.season_name,
						currency,
						cab_types: season.cab_types?.map((cab_type) => cab_type.name),
						intervals: season.date_ranges?.map((date_range) => {
							return {
								start_date:
									date_range.start_date &&
									formatDate(
										startOf(date_range.start_date, "day"),
										"YYYY-MM-DD"
									),
								end_date:
									date_range.end_date &&
									formatDate(startOf(date_range.end_date, "day"), "YYYY-MM-DD"),
							}
						}),
					}
				}),
				data: transport_price_services.map((service) => {
					return {
						transport_service_location: service.transport_service_location?.id,
						transport_services: service.transport_service_types.map(
							(transport_type) => {
								return {
									transport_service: transport_type.transport_service_type?.id,
									seasons: seasons.map((season) => {
										return {
											prices: transport_type.seasons[season.season_id]?.map(
												(cab_price) => {
													return {
														cab_type: season.cab_types.find(
															(cab_type) =>
																cab_type.id === cab_price.cab_type_id
														)?.name,
														amount:
															(cab_price.price as number) < 0
																? null
																: cab_price.price || null,
													}
												}
											),
										}
									}),
								}
							}
						),
					}
				}),
			}
			return xhr
				.post(`/data-drive/transport-services/files`, {
					...fileDataPayload,
				})
				.then((resp) => resp.data)
		},

		async uploadFile(
			fileVersionId: string
		): Promise<TSheetFileVersion<TFileData>> {
			return xhr
				.post(`/data-drive/file-versions/${fileVersionId}/upload`)
				.then((resp) => resp.data.data)
		},
	}
}
